import CustomButton from "components/Atoms/CustomButton"
import CustomTag from "components/Atoms/CustomTag"
import Toggle from "components/Atoms/Toggle"
import Error from "components/Common/Error"
import CustomOverflowList from "components/Molecules/CustomOverflowList"
import SerialColumn from "components/Organisms/table/SerialColumn"
import CustomReactTable from "components/Templates/CustomReactTable"
import ModuleHeader from "components/Templates/ModuleHeader"
import {
  useAddUpdateStorageLocationMutation,
  useDcStorageLocationsQuery,
} from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import OverFlowTag from "pages/scheduling-new/table/OverFlowTag"
import React, { useMemo, useState } from "react"
import AppToaster from "utils/AppToaster"
import AddStorageLocationDialog from "./AddStorageLocationDialog"
import useDCStore from "./dc.store"

const StorageLocations = () => {
  const dcId = useDCStore(store => store.state.basicDetails.id)

  const [{ search, page }, setFilter] = useSearchFilter()
  const [isOpen, setOpen] = useState(false)
  const [item, setItem] = useState(null)

  const queryVariables = useMemo(
    () => ({
      query: search ?? "",
      first: 13,
      skip: page * 13,
      filter: { distributionCentreId: dcId },
    }),
    [search, page, dcId]
  )
  const { data, loading, error, refetch } = useDcStorageLocationsQuery({
    variables: queryVariables,
  })

  const COLUMNS = [
    SerialColumn,
    {
      width: 100,
      Header: "Status",
      Cell({ original }) {
        const archived = original?.archive
        const [toggle] = useAddUpdateStorageLocationMutation({
          onCompleted({ addUpdateDcStorageLocation: { storageLocation } }) {
            AppToaster.show({
              intent: "success",
              message: `Storage Location ${storageLocation.name} is now ${
                storageLocation.archive ? "archived" : "active"
              }`,
            })
          },
        })

        if (original?.id?.startsWith("vt")) return <></>
        return (
          <Toggle
            intent="success"
            checked={!archived}
            onChange={() => {
              toggle({
                variables: {
                  name: original?.name,
                  codename: original?.codename,
                  colorCode: original?.colorCode,
                  archive: !archived,
                  distributionCentreId: dcId,
                  responsiblePersonId: original?.responsiblePerson?.id,
                  id: original?.id,
                },
              })
            }}
            innerLabel={archived ? "Archived" : "Active"}
          />
        )
      },
    },
    {
      value: "id",
      Header: "",
      width: 50,
      Cell({ original }) {
        return (
          <CustomButton
            minimal={true}
            icon="edit"
            onClick={() => {
              setItem(original)
              setOpen(true)
            }}
          />
        )
      },
    },
    {
      Header: "Color",
      accessor: "colorCode",
      width: 60,
      Cell({ value }) {
        return (
          <div
            style={{
              borderRadius: 4,
              background: `${value}`,
              height: 26,
              width: 26,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        )
      },
    },
    {
      Header: "Location",
      accessor: "name",
      width: 300,
      Cell({ original }) {
        return (
          <div className="text-left">
            <span className="mx-2">{original.codename}</span>
            <span className="vt-highlight"> {original.name}</span>
          </div>
        )
      },
    },
    {
      Header: "Sub-Locations",
      accessor: "subLocations",
      Cell({ original }) {
        return (
          <CustomOverflowList
            items={original.subLocations}
            visibleItemRenderer={(item: any) => {
              return (
                <div>
                  <CustomTag className="mr-1" round minimal key={item?.id}>
                    {item?.name} - {item?.codename}
                  </CustomTag>
                </div>
              )
            }}
            overflowRenderer={extraDepartments => {
              return (
                <OverFlowTag
                  className="mr-2"
                  renderText={v => v?.name}
                  items={extraDepartments}
                />
              )
            }}
          />
        )
      },
    },
    {
      Header: "Stock take responsible",
      accessor: "responsiblePerson",
      Cell({ value }) {
        return `${value?.firstName} ${value?.lastName}`
      },
    },
  ]

  if (error) return <Error message={error?.message} />

  return (
    <>
      <ModuleHeader
        header={
          <>
            <span>Storage Locations</span>
          </>
        }
        borderBottom="none"
        query={search ?? ""}
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        white
        className="p-3"
      >
        <CustomButton
          text="Location"
          icon="plus"
          dark={true}
          onClick={() => {
            setItem(null)
            setOpen(true)
          }}
        />
      </ModuleHeader>
      <AddStorageLocationDialog
        key={item?.codename}
        item={item}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        refreshData={refetch}
      />
      <CustomReactTable
        loading={loading}
        data={data?.allDcStorageLocations}
        columns={COLUMNS}
      />
    </>
  )
}

export default StorageLocations
